<template>
  <div class="rbz-time-picker" ref="triggerRef">
    <input
      class="rbz-time-picker-trigger"
      :style="{ '--chars': `${formattedDateRange.length}ch` }"
      :value="formattedDateRange"
      readonly
      data-qa="time-picker-trigger"
      aria-haspopup="dialog"
      @click="() => openPanel()"
      @keydown.enter.prevent="() => openPanel()"
      @keydown.space.prevent="() => openPanel()"
    />
    <span class="rbz-time-picker-trigger-icon" aria-hidden="true" @click="() => openPanel()">
      <icon-calendar color="shuttle-gray" />
    </span>

    <!-- Panel teleported to body to avoid overflow clipping -->
    <Teleport to="body">
      <div
        v-if="panelOpen"
        class="rbz-time-picker-panel"
        ref="panelRef"
        :style="panelPositionStyle"
        data-qa="time-picker-panel"
      >
        <!-- ── Left column: Relative presets + Live data + Refresh interval ── -->
        <div class="panel-left">
          <p class="section-header">{{ t('timePicker.relativeTimeRange') }}</p>

          <div class="preset-list">
            <button
              v-for="preset in presets"
              :key="preset.label"
              class="preset-btn"
              :class="{ 'preset-btn--selected': selectedPreset?.label === preset.label }"
              type="button"
              @click="handlePresetClick(preset)"
            >
              {{ preset.label }}
            </button>
          </div>

          <!-- Live data toggle -->
          <div class="live-section">
            <div class="live-toggle-row">
              <div class="live-dot-wrapper" aria-hidden="true">
                <span class="live-dot-pulse" />
                <span class="live-dot" />
              </div>
              <span class="live-label">{{ t('datePicker.liveData') }}</span>
              <form-toggle
                v-model:button-value="liveEnabled"
                size="small"
                :disabled="!selectedPreset"
                :toggle-tooltip="!selectedPreset ? t('datePicker.liveDataDisabledTooltip') : ''"
                data-qa="live-data-toggle"
              />
            </div>
            <p v-if="liveEnabled && selectedPreset" class="live-status-text">
              {{ t('timePicker.autoRefreshEvery', { interval: activeIntervalLabel }) }}
            </p>
          </div>

          <!-- Refresh interval (visible only when live is on) -->
          <div v-if="liveEnabled && selectedPreset" class="interval-section">
            <p class="section-header">{{ t('timePicker.refreshEvery') }}</p>
            <div class="interval-pills">
              <button
                v-for="opt in INTERVAL_OPTIONS"
                :key="opt.seconds"
                class="interval-pill"
                :class="{ 'interval-pill--selected': refreshIntervalSeconds === opt.seconds }"
                type="button"
                @click="refreshIntervalSeconds = opt.seconds"
              >
                {{ opt.label }}
              </button>
            </div>
          </div>
        </div>

        <!-- ── Right column: Absolute time range ── -->
        <div class="panel-right">
          <p class="panel-right-title">{{ t('timePicker.absoluteTimeRange') }}</p>
          <p v-if="selectedPreset && liveEnabled" class="selected-preset-hint">
            {{ t('timePicker.applyingLiveHint', { label: selectedPreset.label }) }}
          </p>

          <DateTimeInput v-model="fromDateIso" :label="t('datePicker.fromLabel')" />
          <DateTimeInput v-model="toDateIso" :label="t('datePicker.toLabel')" />

          <p class="absolute-warning">{{ t('timePicker.absoluteDisablesLive') }}</p>

          <div class="panel-actions">
            <form-button
              variant="secondary"
              :label="t('common.operations.cancel.label')"
              :ignore-update-in-process="true"
              data-qa="cancel-button"
              @button-click="closePanel"
            />
            <form-button
              variant="primary"
              :label="applyButtonLabel"
              :ignore-update-in-process="true"
              data-qa="apply-button"
              @button-click="apply"
            />
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { onUnmounted, ref, toRef } from 'vue';
import { useI18n } from 'vue-i18n';
import FormButton from '../components/FormButton.vue';
import FormToggle from '../components/FormToggle.vue';
import IconCalendar from '../../assets/icons/IconCalendar.vue';
import DateTimeInput from './DateTimeInput.vue';
import { useTimeRangePicker, INTERVAL_OPTIONS } from '../composables/useTimeRangePicker';
import type { Preset, LiveModePayload } from '../composables/useTimeRangePicker';

const { t } = useI18n();

export type { Preset, LiveModePayload };

interface Props {
  defaultTime?: number;
}

const props = withDefaults(defineProps<Props>(), {
  defaultTime: 60,
});

interface Emits {
  (e: 'update:date', value: [string, string]): void;
  (e: 'update:live-mode', value: LiveModePayload): void;
}

const emit = defineEmits<Emits>();

const triggerRef = ref<HTMLElement | null>(null);
const panelRef = ref<HTMLElement | null>(null);
const panelOpen = ref(false);
const panelPositionStyle = ref<Record<string, string>>({});

// External anchor element: clicks on this element should not close the panel
// (same as clicking the trigger button).
let _externalAnchorEl: HTMLElement | null = null;
let _clickListenerTimeoutId: ReturnType<typeof setTimeout> | null = null;

const handleClickOutside = (e: MouseEvent) => {
  const target = e.target as Node;
  // Allow clicks inside any teleported datepicker calendar menus
  const dpMenus = document.querySelectorAll('.dp__menu');
  for (const menu of dpMenus) {
    if (menu.contains(target)) return;
  }
  if (panelRef.value?.contains(target)) return;
  if (triggerRef.value?.contains(target)) return;
  if (_externalAnchorEl?.contains(target)) return;
  closePanel();
};

const _applyPanelPosition = (anchorRect?: DOMRect) => {
  const rect = anchorRect ?? triggerRef.value?.getBoundingClientRect();
  if (rect) {
    panelPositionStyle.value = {
      top: `${rect.bottom + 4}px`,
      right: `${window.innerWidth - rect.right}px`,
    };
  }
};

const closePanel = () => {
  panelOpen.value = false;
  _externalAnchorEl = null;
  if (_clickListenerTimeoutId !== null) {
    clearTimeout(_clickListenerTimeoutId);
    _clickListenerTimeoutId = null;
  }
  document.removeEventListener('click', handleClickOutside, { capture: true });
};

const {
  currentDate,
  fromDateIso,
  toDateIso,
  presets,
  selectedPreset,
  liveEnabled,
  refreshIntervalSeconds,
  formattedDateRange,
  activeIntervalLabel,
  applyButtonLabel,
  loadPresets,
  detectCurrentPreset,
  syncAbsoluteFieldsFromCurrentDate,
  handlePresetClick,
  apply,
  changeTime,
  disableLive,
  resetTimeToDefault,
} = useTimeRangePicker({
  defaultTime: toRef(props, 'defaultTime'),
  onEmitDate: (val) => emit('update:date', val),
  onEmitLiveMode: (val) => emit('update:live-mode', val),
  onClose: closePanel,
});

const openPanel = (anchorRect?: DOMRect, externalAnchorEl?: HTMLElement | null) => {
  _externalAnchorEl = externalAnchorEl ?? null;

  loadPresets();
  syncAbsoluteFieldsFromCurrentDate();
  detectCurrentPreset();
  _applyPanelPosition(anchorRect);

  panelOpen.value = true;
  // Defer so the originating click doesn't immediately close the panel.
  // Store the id so it can be cancelled if the panel closes before it fires.
  _clickListenerTimeoutId = setTimeout(() => {
    _clickListenerTimeoutId = null;
    if (!panelOpen.value) return;
    document.addEventListener('click', handleClickOutside, { capture: true });
  }, 0);
};

/**
 * Open the panel pre-populated with a running live-mode state.
 * Called by LiveTimeIndicator when the user wants to adjust settings.
 */
const openPanelWithState = (
  liveModeActive: boolean,
  minutes: number,
  intervalSeconds: number,
  anchorRect?: DOMRect,
  externalAnchorEl?: HTMLElement | null
) => {
  _externalAnchorEl = externalAnchorEl ?? null;

  loadPresets();
  liveEnabled.value = liveModeActive;
  refreshIntervalSeconds.value = intervalSeconds;
  selectedPreset.value = presets.value.find((p) => p.minutes === minutes) ?? null;

  if (selectedPreset.value) {
    const now = new Date();
    const start = new Date(now.getTime() - minutes * 60_000);
    fromDateIso.value = start.toISOString();
    toDateIso.value = now.toISOString();
  } else {
    syncAbsoluteFieldsFromCurrentDate();
  }

  _applyPanelPosition(anchorRect);
  panelOpen.value = true;
  _clickListenerTimeoutId = setTimeout(() => {
    _clickListenerTimeoutId = null;
    if (!panelOpen.value) return;
    document.addEventListener('click', handleClickOutside, { capture: true });
  }, 0);
};

defineExpose({
  currentDate,
  changeTime,
  disableLive,
  resetTimeToDefault,
  openPanel,
  openPanelWithState,
});

onUnmounted(() => {
  if (_clickListenerTimeoutId !== null) {
    clearTimeout(_clickListenerTimeoutId);
  }
  document.removeEventListener('click', handleClickOutside, { capture: true });
});
</script>

<style src="@vuepic/vue-datepicker/dist/main.css"></style>

<style lang="scss" scoped>
@use '../../assets/styles/colors' as colors;
@use 'sass:map';

$black-25: map.get(colors.$colors, 'black-25');
$shamrock: map.get(colors.$colors, 'shamrock');
$shamrock-0: map.get(colors.$colors, 'shamrock-0');
$shamrock-70: map.get(colors.$colors, 'shamrock-70');
$dusty-gray: map.get(colors.$colors, 'dusty-gray');
$silver: map.get(colors.$colors, 'silver');
$mercury: map.get(colors.$colors, 'mercury');
$wild-sand: map.get(colors.$colors, 'wild-sand');

.rbz-time-picker {
  align-items: center;
  display: inline-flex;
  position: relative;
}

.rbz-time-picker-trigger {
  border: 1px solid $silver;
  border-radius: 4px;
  box-shadow: 0 0 0 $black-25;
  color: map.get(colors.$colors, 'shuttle-gray');
  cursor: pointer;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  height: 40px;
  line-height: 20px;
  outline: none;
  padding-left: 12px;
  padding-right: 44px;
  width: calc(var(--chars) + 25px);

  &:hover {
    background: map.get(colors.$colors, 'selago');
    border-color: map.get(colors.$colors, 'silver');
  }

  &:focus-visible {
    border-color: map.get(colors.$colors, 'jewel');
    outline: 2px solid map.get(colors.$colors, 'magic-mint');
    outline-offset: 1px;
  }
}

.rbz-time-picker-trigger-icon {
  cursor: pointer;
  height: 24px;
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);

  :deep(svg) {
    display: block;
  }
}

.rbz-time-picker-panel {
  background: map.get(colors.$colors, 'alabaster');
  border-radius: 4px;
  box-shadow: 0 4px 4px $black-25;
  display: flex;
  position: fixed;
  z-index: 9000;
}

.panel-left {
  border-right: 1px solid $mercury;
  display: flex;
  flex-direction: column;
  width: 210px;
}

.section-header {
  color: map.get(colors.$colors, 'dusty-gray');
  font-size: 11px;
  font-weight: 400;
  letter-spacing: 0.55px;
  line-height: 16.5px;
  margin: 0;
  padding: 16.5px 16px 6px;
  text-transform: uppercase;
}

.preset-list {
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 0 8px 8px;
}

.preset-btn {
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 4px;
  color: map.get(colors.$colors, 'shuttle-gray');
  cursor: pointer;
  display: flex;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  height: 40px;
  line-height: 21px;
  padding: 9px 69px 9px 16px;
  text-align: left;
  transition: background-color 0.1s ease;
  width: 100%;

  &:hover {
    background-color: map.get(colors.$colors, 'white-ice');
  }

  &:focus:not(:active) {
    // border: 1px solid $magic-mint;
    outline: none;
  }

  &--selected {
    background-color: map.get(colors.$colors, 'hint-of-green');
    color: map.get(colors.$colors, 'san-felix');

    /* stylelint-disable selector-class-pattern, selector-no-qualifying-type */
    .preset-btn__icon {
      visibility: visible;
    }
    /* stylelint-enable selector-class-pattern, selector-no-qualifying-type */

    &:hover {
      background-color: map.get(colors.$colors, 'hint-of-green');
    }
  }
}

// Live data section
.live-section {
  border-top: 1px solid $mercury;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 13px 16px;
}

.live-toggle-row {
  align-items: center;
  display: flex;
  gap: 8px;
  justify-content: space-between;
}

.live-dot-wrapper {
  flex-shrink: 0;
  height: 8px;
  position: relative;
  width: 8px;
}

.live-dot-pulse {
  animation: ltp-ping 1.5s ease-out infinite;
  border-radius: 50%;
  display: block;
  height: 8px;
  left: 0;
  position: absolute;
  top: 0;
  width: 8px;
}

.live-dot {
  animation: ltp-glow 1.5s ease-in-out infinite;
  background: map.get(colors.$colors, 'shamrock');
  border-radius: 50%;
  display: block;
  height: 8px;
  left: 0;
  position: absolute;
  top: 0;
  width: 8px;
}

@keyframes ltp-ping {
  0% {
    box-shadow: 0 0 0 0 $shamrock-70;
  }

  70% {
    box-shadow: 0 0 0 6px $shamrock-0;
  }

  100% {
    box-shadow: 0 0 0 0 $shamrock-0;
  }
}

@keyframes ltp-glow {
  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.65;
  }
}

.live-label {
  color: map.get(colors.$colors, 'shuttle-gray');
  flex: 1;
  font-size: 14px;
  font-weight: 400;
  line-height: 21px;
}

.live-status-text {
  color: map.get(colors.$colors, 'jewel');
  font-size: 11px;
  line-height: 16.5px;
  margin: 0;
}

// ── Refresh interval section ────
.interval-section {
  border-top: 1px solid $mercury;
  padding: 0 16px 16px;
}

.interval-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 8px;
}

.interval-pill {
  background: $wild-sand;
  border: 0;
  border-radius: 4px;
  color: map.get(colors.$colors, 'shuttle-gray');
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  height: 26px;
  line-height: 18px;
  min-width: 34px;
  padding: 4px 8px;
  transition: background-color 0.1s ease;

  &:hover {
    background: $mercury;
  }

  &--selected {
    background: map.get(colors.$colors, 'jewel');
    color: map.get(colors.$colors, 'white');

    &:hover {
      background: map.get(colors.$colors, 'jewel');
    }
  }
}

.panel-right {
  background: map.get(colors.$colors, 'white');
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 23px;
  width: 330px;
}

.panel-right-title {
  color: map.get(colors.$colors, 'mine-shaft');
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  margin: 0;
}

.absolute-warning {
  color: map.get(colors.$colors, 'dusty-gray');
  font-size: 11px;
  line-height: 16.5px;
  margin: 0;
}

.selected-preset-hint {
  color: map.get(colors.$colors, 'jewel');
  font-size: 11px;
  line-height: 16.5px;
  margin: -8px 0 0;
}

.panel-actions {
  display: flex;
  gap: 14px;
  justify-content: flex-end;
  margin-top: auto;
  padding-top: 16px;
}
</style>

<style lang="scss">
/* stylelint-disable selector-class-pattern */
@use '../../assets/styles/colors' as colors;
@use 'sass:map';
@use 'sass:color';

$black-25: map.get(colors.$colors, 'black-25');

.dp__menu {
  background-color: map.get(colors.$colors, 'alabaster');
  border-radius: 4px;
  box-shadow: 0 4px 4px $black-25;

  .dp__arrow_top {
    display: none;
  }
}

.dp__menu_inner {
  gap: 12px;
  padding: 0;

  .dp__instance_calendar {
    background-color: map.get(colors.$colors, 'white');
    padding: 16px 20px;

    .dp__calendar_next {
      margin-left: 0;
    }
  }
}

.dp--header-wrap {
  margin-bottom: 8px;
  position: relative;

  .dp__month_year_wrap {
    color: map.get(colors.$colors, 'bright-gray');
    font-size: 16px;
    justify-content: center;
    line-height: 20px;

    .dp__month_year_select {
      width: fit-content;
    }

    .dp__overlay_container {
      background: map.get(colors.$colors, 'white');
      position: relative;
      z-index: 1000;
    }
  }

  .dp--arrow-btn-nav {
    position: absolute;
    top: 3px;

    &:first-child {
      left: 0;
    }

    &:last-child {
      right: 0;
    }

    .dp__icon {
      fill: map.get(colors.$colors, 'stratos');
    }
  }

  .dp__btn {
    &:hover {
      background-color: map.get(colors.$colors, 'white-ice');
    }

    &:focus:not(:active) {
      box-shadow: 0 0 0 2px map.get(colors.$colors, 'magic-mint');
      outline: none;
      z-index: 1;
    }
  }

  .dp__inner_nav {
    background: transparent;
  }
}

.dp__calendar_header {
  color: map.get(colors.$colors, 'jewel');
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  margin-bottom: 3px;

  .dp__calendar_header_item {
    align-items: center;
    display: flex;
    height: 24px;
    justify-content: center;
    padding: 0;
  }
}

.dp__calendar_header_separator {
  display: none;
}

.dp__calendar {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: 5px;

  .dp__calendar_row {
    justify-content: space-around;
    margin: 0;

    .dp__calendar_item {
      flex: 0 0 36px;
      padding: 0;
    }

    .dp__cell_inner {
      height: 36px;
      width: 36px;
    }
  }

  .dp__calendar_item {
    color: map.get(colors.$colors, 'shuttle-gray');
    font-size: 14px;
    line-height: 18px;

    &:hover {
      background-color: map.get(colors.$colors, 'white-ice');
      color: map.get(colors.$colors, 'shuttle-gray');
    }

    &:focus:not(:active) {
      box-shadow: 0 0 0 2px map.get(colors.$colors, 'magic-mint');
      outline: none;
      z-index: 1;
    }
  }

  .dp__cell_offset {
    color: map.get(colors.$colors, 'silver');
  }

  .dp__active_date {
    background-color: map.get(colors.$colors, 'jewel');
    color: map.get(colors.$colors, 'white');
  }

  .dp__today {
    border: 1px solid map.get(colors.$colors, 'jewel');
  }
}

.dp__selection_preview {
  color: map.get(colors.$colors, 'shuttle-gray');
  flex: 1;
  font-size: 13px;

  // VueDatePicker sets max-width inline via JS — must override with !important
  max-width: none !important;
  min-width: 0;
  overflow: visible;
  white-space: nowrap;
}

.dp__action_row {
  border-top: 1px solid map.get(colors.$colors, 'silver');
  padding: 12px 16px;
  width: 100%;
}

.dp__action_button {
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  height: 32px;
  padding: 0 16px;
}

.dp__action_cancel {
  background: map.get(colors.$colors, 'white');
  border: 1px solid map.get(colors.$colors, 'silver');
  color: map.get(colors.$colors, 'shuttle-gray');

  &:hover {
    background: map.get(colors.$colors, 'selago');
    border-color: map.get(colors.$colors, 'silver');
  }
}

// Higher specificity required: VueDatePicker scopes this as .dp__action_buttons .dp__action_select
.dp__action_buttons .dp__action_select {
  background: map.get(colors.$colors, 'jewel');
  border: 0;
  color: map.get(colors.$colors, 'white');

  &:hover {
    background: color.adjust(map.get(colors.$colors, 'jewel'), $lightness: -8%);
  }

  &:disabled {
    background: map.get(colors.$colors, 'dusty-gray');
    cursor: not-allowed;
  }
}
</style>
